/**
 * Big Search API — busca unificada staff-only (Admin/Super Admin).
 *
 * Backend: modules/big-search (branch feat/big-search). Envelope:
 * `{ status, success, message, data: { results, totalCount, page, limit, ... } }`
 * → nos consumidores, SEMPRE guardar com Array.isArray antes de mapear.
 */
import { http } from "./client";
import type { EventPresentationState } from "../utils/methods/eventPresentation";

export interface BigSearchRoleEntry {
  role: string;
  status: string;
}

/**
 * Fields consumed by the shared contact card (`components/Tooltip/ContactTool`),
 * the same popover the Event Details screens show on hover. Backend twin:
 * `CONTACT_CARD_FIELDS` in `big-search.pipelines.ts` — add a field to the card
 * and it has to be added on BOTH sides.
 */
export interface BigSearchContactCard {
  preferredName?: string | null;
  contractorType?: string | null;
  companyName?: string | null;
  /** "Contractor ID" on the card. */
  profileId?: string | null;
  workBookId?: string | null;
  /** "HR Approved" on the card. */
  isGustoApproved?: boolean | null;
  /** rendered together as "Nashville, TN" under the phone. */
  city?: string | null;
  state?: string | null;
  /** most-worked positions (shift history), resolved server-side. */
  proficientRoles?: string[] | null;
  /** with a Role filter: why the person matched (worked / declared). */
  matchedRoles?: Array<{ name: string; source: "worked" | "declared"; shifts?: number; years?: number }> | null;
  /** free-text fallback when the person has no positions registered. */
  rolesWorked?: string | null;
  smsPermissionStatus?: boolean | null;
  smsConsentStatusBySms?: boolean | null;
  smsOptedOut?: boolean | null;
  isContractorFlagged?: boolean | null;
  flaggedRemark?: string | null;
  isContractorRestricted?: boolean | null;
  restrictedRemark?: string | null;
}

export interface BigSearchPerson extends BigSearchContactCard {
  _id: string;
  fullName?: string;
  email?: string;
  mobile?: string;
  isActive?: boolean;
  activeRole?: string | null;
  roles?: BigSearchRoleEntry[];
}

/** Vínculo da pessoa com o evento — "trabalhou ≠ coordenou". */
export type BigSearchInvolvement = "all" | "worked" | "coordinated";

/** Quebra por vínculo sobre o conjunto inteiro (independe do filtro/página). */
export interface BigSearchPersonEventCounts {
  total: number;
  worked: number;
  coordinated: number;
  both: number;
}

export interface BigSearchPersonEvent extends EventPresentationState {
  contactInformationId?: string | null;
  assignedCoordinatorId?: string | string[] | null;
  eventId: string;
  eventName?: string;
  jobNumber?: number;
  eventStartDate?: string;
  eventEndDate?: string;
  eventStart?: string;
  eventEnd?: string;
  venue?: string;
  venueCity?: string;
  venueState?: string;
  status?: string;
  positionNames?: string[];
  roles?: string[];
  shifts?: number;
  /** escalado como crew (assignedContractor). */
  worked?: boolean;
  /** coordenador designado do evento (assignedCoordinatorId). === isCoordinator. */
  coordinated?: boolean;
  isCoordinator?: boolean;
  firstShiftDate?: string;
  lastShiftDate?: string;
}

export interface BigSearchEvent extends EventPresentationState {
  contactInformationId?: string | null;
  assignedCoordinatorId?: string | string[] | null;
  _id: string;
  eventName?: string;
  jobNumber?: number;
  eventStartDate?: string;
  eventEndDate?: string;
  eventStart?: string;
  eventEnd?: string;
  venue?: string;
  venueCity?: string;
  venueState?: string;
  status?: string;
}

export interface BigSearchAssignedBy {
  personId: string;
  name: string;
}

export interface BigSearchStaffMember extends BigSearchContactCard {
  personId: string;
  name?: string;
  email?: string | null;
  mobile?: string | null;
  isActive?: boolean;
  roles?: string[];
  isCoordinator?: boolean;
  shifts?: number;
  sessionsCount?: number;
  firstShiftDate?: string | null;
  lastShiftDate?: string | null;
  assignedBy?: BigSearchAssignedBy[];
}

export interface BigSearchEventDetails extends Omit<BigSearchEvent, "_id"> {
  eventId: string;
  coordinators?: BigSearchAssignedBy[];
}

/** Status do PO (savedpos.poStatus / ec_rates.poStatus). `null` = sem PO ainda. */
export type BigSearchPoStatus =
  | "sent"
  | "seen"
  | "approved"
  | "auto-approved"
  | "rejected"
  | "paymentScheduled"
  | "paid"
  | "cancelled"
  | "pending";

export interface BigSearchPayment {
  personId: string;
  /** `po` = contractor (savedpos) · `ec_rate` = Freelance EC (ec_rates). */
  source: "po" | "ec_rate";
  poStatus: BigSearchPoStatus | string | null;
  poNumber?: string | null;
  amount?: number | null;
  paidDate?: string | null;
  paymentScheduledOn?: string | null;
  acceptedAt?: string | null;
  /** só em ec_rate: rate cadastrado mas PO ainda não gerado. */
  poGenerated?: boolean;
  /** >1 = PO regerado; o status é o do doc corrente. */
  poCount?: number;
  updatedAt?: string | null;
}

/** Mapa personId → pagamento. Indexar por chave, nunca por índice de array. */
export type BigSearchPaymentMap = Record<string, BigSearchPayment>;

type PageParams = {
  page?: number | string;
  limit?: number | string;
}

/**
 * GET /big-search/people — pessoas por nome, email ou telefone (q ≥ 2 chars).
 * `states`/`cities`/`positionIds` (CSV, ver utils/methods/crewFilter): com algum
 * deles o q fica opcional.
 */
export const searchBigSearchPeople = (
  params: PageParams & { q?: string; states?: string; cities?: string; positionIds?: string },
) =>
  http({ method: "GET", url: "big-search/people", params, isToken: true });

/**
 * GET /big-search/people/:personId/events — eventos ligados à pessoa.
 * `involvement` separa worked (escala) de coordinated (coordenação); a resposta
 * traz `counts` {total,worked,coordinated,both}.
 */
export const fetchBigSearchPersonEvents = (
  personId: string,
  params?: PageParams & { involvement?: BigSearchInvolvement },
) =>
  http({ method: "GET", url: `big-search/people/${personId}/events`, params, isToken: true });

/** GET /big-search/events — eventos por nome/jobNumber/venue (q vazio = recentes). */
export const searchBigSearchEvents = (params: PageParams & { q?: string }) =>
  http({ method: "GET", url: "big-search/events", params, isToken: true });

/** GET /big-search/events/:eventId/staff — detalhes + quem trabalhou + quem escalou. */
export const fetchBigSearchEventStaff = (
  eventId: string,
  params?: PageParams & { q?: string },
) =>
  http({ method: "GET", url: `big-search/events/${eventId}/staff`, params, isToken: true });

/**
 * GET /big-search/events/:eventId/payments — status de PO por pessoa no evento.
 *
 * ⚠️ Rota gated no backend por `@RequiresPermission('canManageGusto')`
 * ("Ability to update HR/Onboarding status"); superAdmin bypassa. Quem não tem
 * a permissão recebe **403** — só chamar quando o gate do FE passar, senão o
 * painel loga erro à toa. Sem paginação: devolve o mapa do evento inteiro.
 */
export const fetchBigSearchEventPayments = (eventId: string) =>
  http({ method: "GET", url: `big-search/events/${eventId}/payments`, isToken: true });
